import {Component, OnDestroy, OnInit} from '@angular/core';
import {AsyncPipe} from '@angular/common';
import {MetricCardComponent} from '../shared/components/card/metric-card/metric-card.component';
import {IMetricCard} from '../shared/components/card/metric-card/metric-card.interface';
import {
  selectActivePatientsCount,
  selectNeedAttentionCount,
  selectTodaysEventsCount,
} from './store/metric.selectors';
import {AbstractLayoutPageComponent} from '../shared/abstracts/abstract-layout-page.component';
import {AuthDirective} from '../shared/directives/auth.directive';
import {HandleEffectLoading} from '../shared/decorators/loading.decorator';
import {COMPONENT_STATE} from '../shared/di/component-state.token';
import {ComponentStateService} from '../shared/services/component-state.service';
import {
  CalendarRange,
  DashboardCalendarComponent,
} from './components/dashboard-calendar/dashboard-calendar.component';
import {selectEvents} from '../event/store/event.selectors';
import {readEvents, readEventsFailure, readEventsSuccess} from '../event/store/event.actions';
import {CalendarEventDto} from '../shared/dto';

@Component({
  selector: 'app-dashboard',
  imports: [
    AsyncPipe,
    MetricCardComponent,
    AuthDirective,
    DashboardCalendarComponent,
  ],
  providers: [
    { provide: COMPONENT_STATE, useClass: ComponentStateService },
  ],
  templateUrl: './dashboard.page.html',
  styleUrl: './dashboard.page.scss',
})
export class DashboardPage extends AbstractLayoutPageComponent implements OnInit, OnDestroy {

  cards: IMetricCard[] = [];

  /** Operator-wide events feeding the read-only calendar. */
  readonly events$ = this.store.select(selectEvents);

  /** Last range we requested — guards against redundant reloads on re-render. */
  private _lastRange: string | null = null;

  ngOnInit() {
    this.pageService.hasBreadcrumbs.set(false);
    this._initDashboardCards();
  }

  ngOnDestroy() {
    this.pageService.hasBreadcrumbs.set(true);
  }

  /**
   * Loads the events for the calendar's visible range. Fired once on init (the
   * calendar emits its initial range) and again on every navigation / view change.
   */
  onCalendarRangeChanged(range: CalendarRange): void {
    if (!this.permissionService.hasPermission('ASSIGNMENT_READ')) return;
    const key = `${range.start}|${range.end}`;
    if (key === this._lastRange) return;
    this._lastRange = key;
    this._loadEvents(range);
  }

  onEventClicked(event: CalendarEventDto): void {
    const userId = event.userId;
    if (userId == null) return;
    // Navigate to the patient. Once the event payload carries the member id we
    // can deep-link to the assignment/clinical-diary instead of the user page.
    if (this.permissionService.hasPermission('USER_READ')) {
      this.router.navigate(['..', 'app', 'users', userId])
        .catch(e => this.logger.error(this.constructor.name, e));
    }
  }

  async onMetricCardClicked(callback?: () => any): Promise<void> {
    this.logger.debug(this.constructor.name, 'Card clicked', callback);
    if (typeof callback === 'function') callback();
  }

  @HandleEffectLoading(readEventsSuccess, readEventsFailure)
  private _loadEvents(range: CalendarRange): void {
    this.store.dispatch(readEvents({ start: range.start, end: range.end }));
  }

  private _initDashboardCards(): void {
    this.cards = [
      {
        id: 1,
        text: $localize`Sessioni giornaliere`,
        data: this.store.select(selectTodaysEventsCount),
        iconPrime: 'pi pi-calendar',
        auth: this.createAuthOptions(['ASSIGNMENT_READ']),
      },
      {
        id: 2,
        text: $localize`Pazienti attivi`,
        data: this.store.select(selectActivePatientsCount),
        iconPrime: 'pi pi-users',
        auth: this.createAuthOptions(['ASSIGNMENT_READ']),
      },
      {
        id: 3,
        text: $localize`Richiedono attenzione`,
        data: this.store.select(selectNeedAttentionCount),
        iconPrime: 'pi pi-exclamation-triangle',
        auth: this.createAuthOptions(['ASSIGNMENT_READ']),
      },
    ];
  }

}
